/**
 * Notification Routes Service
 * Maps notification types to frontend routes
 * Frontend constructs URLs instead of storing them in the database
 */

import type { UserNotificationDTO } from '@/types';

/**
 * Route mapping for each notification type
 * Each function receives the notification DTO and returns the appropriate route
 */
export const notificationRoutes: Record<string, (notification: UserNotificationDTO) => string> = {
  // Provisioning notifications
  PROVISIONING_REQUEST_NEW: (notification) => `/project/${notification.projectId}?component=budget`,
  PROVISIONING_REQUEST_APPROVED: (notification) => `/project/${notification.projectId}?component=budget`,
  PROVISIONING_REQUEST_REJECTED: (notification) => `/project/${notification.projectId}?component=budget`,

  // Project notifications
  PROJECT_INVITATION: (notification) => `/project/${notification.projectId}?component=access`,
  PROJECT_CREATED: (notification) => `/project/${notification.projectId}?component=info`,
  PROJECT_STATUS_CHANGED: (notification) => `/project/${notification.projectId}?component=info`,
  PROJECT_DEADLINE_APPROACHING: (notification) => `/project/${notification.projectId}?component=info`,

  // Task notifications
  TASK_ASSIGNMENT: (notification) => `/project/${notification.projectId}?component=workspace`,
  TASK_COMPLETED: (notification) => `/project/${notification.projectId}?component=workspace`,
  TASK_OVERDUE: (notification) => `/project/${notification.projectId}?component=workspace`,
  TASK_STATUS_CHANGED: (notification) => `/project/${notification.projectId}?component=workspace`,

  // Payment notifications
  PAYMENT_RECEIVED: (notification) => `/project/${notification.projectId}?component=budget`,
  PAYMENT_DUE: (notification) => `/project/${notification.projectId}?component=budget`,
  PAYMENT_OVERDUE: (notification) => `/project/${notification.projectId}?component=budget`,

  // Document notifications
  DOCUMENT_SHARED: (notification) => `/project/${notification.projectId}?component=documents`,
  DOCUMENT_APPROVAL_REQUIRED: (notification) => `/project/${notification.projectId}?component=documents`,
  DOCUMENT_APPROVED: (notification) => `/project/${notification.projectId}?component=documents`,
  DOCUMENT_REJECTED: (notification) => `/project/${notification.projectId}?component=documents`,

  // Phase notifications
  PHASE_STATUS_CHANGED: (notification) => `/project/${notification.projectId}?component=workspace`,
  PHASE_COMPLETED: (notification) => `/project/${notification.projectId}?component=workspace`,
  PHASE_DEADLINE_APPROACHING: (notification) => `/project/${notification.projectId}?component=workspace`,

  // System notifications
  SYSTEM_ANNOUNCEMENT: () => '/dashboard',
  SYSTEM_MAINTENANCE: () => '/dashboard',

  // Subscription notifications
  SUBSCRIPTION_RENEWAL_ORDER: () => '/my-subscription',
  SUBSCRIPTION_RENEWAL_SUCCESS: () => '/my-subscription',
  SUBSCRIPTION_EXPIRING_SOON: () => '/my-subscription',
};

/**
 * Get the navigation route for a notification
 * @param notification - The notification DTO
 * @returns The frontend route to navigate to
 */
export function getNotificationRoute(notification: UserNotificationDTO): string {
  const routeFn = notificationRoutes[notification.type];
  
  if (!routeFn) {
    console.warn(`No route defined for notification type: ${notification.type}`);
    return '/dashboard';
  }
  
  return routeFn(notification);
}

/**
 * Check if a notification has a specific action route
 * @param notificationType - The notification type
 * @returns True if the notification type has a defined route
 */
export function hasNotificationRoute(notificationType: string): boolean {
  return notificationType in notificationRoutes;
}
